import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, styled } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import './Dashboard.css';

// スタイリングコンポーネントを定義
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  }
}));

const StyledTypographyValue = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '1.5rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.8rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2rem',
  }
}));

const CorporateDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    shop_name: '',
    ads_stats: {
      total_ads: 0,
      active_ads: 0,
      expired_ads: 0
    },
    engagement_stats: {
      total_views: 0,
      recent_views: 0,
      total_likes: 0
    },
    linked_users_count: 0,
    region_views: [],
    hourly_views: []
  });
  
  const [weeklyData, setWeeklyData] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // APIからダッシュボードデータを取得
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          },
          // リクエスト失敗時に詳細情報を得るために validateStatus を追加
          validateStatus: function (status) {
            return status < 500; // 500未満のステータスはエラーとして扱わない
          }
        });
        
        if (response.data && response.data.status === 'success') {
          setDashboardData(response.data.data);
          
          // 週間データを設定（APIレスポンスを使用）
          if (response.data.data.hourly_views && response.data.data.hourly_views.length > 0) {
            // バックエンドから日別データが返される場合はそのまま使用
            const weekData = response.data.data.hourly_views.map(item => ({
              day: item.day,
              閲覧数: item.views || item['閲覧数'] || 0,
              いいね: item.likes || item['いいね'] || 0
            }));
            setWeeklyData(weekData);
          } else {
            setWeeklyData(generateMockWeeklyData());
          }
        } else {
          // エラーがあるか、データ形式が予期しないもの
          throw new Error('APIからの応答が無効です');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('企業ダッシュボードAPI エラー詳細:', err);
        console.error('レスポンス:', err.response);
        console.error('ステータス:', err.response?.status);
        console.error('データ:', err.response?.data);
        
        // エラーメッセージを詳細に表示
        let errorMessage = 'データの読み込み中にエラーが発生しました。';
        if (err.response?.status === 403) {
          errorMessage = '企業ユーザー権限が必要です。';
        } else if (err.response?.status === 401) {
          errorMessage = '認証エラーが発生しました。再ログインしてください。';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
        
        setError(errorMessage);
        setLoading(false);
        
        // デモデータを設定（開発用）
        setDashboardData({
          shop_name: 'デモ店舗',
          ads_stats: {
            total_ads: 12,
            active_ads: 5,
            expired_ads: 7
          },
          engagement_stats: {
            total_views: 1245,
            recent_views: 320,
            total_likes: 876
          },
          linked_users_count: 8,
          region_views: [],
          hourly_views: []
        });
        
        setWeeklyData(generateMockWeeklyData());
      }
    };
    
    fetchDashboardData();
  }, [token]);
  
  // 時間別データを週間データに変換する関数
  const convertHourlyToWeeklyData = (hourlyData) => {
    // ダミーデータですが、実際のAPIデータを週間データに変換するロジックを実装
    const days = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];
    return days.map((day, index) => {
      // 時間帯データをグループ化して日別に集計
      const startHour = index * 24/7;
      const endHour = (index + 1) * 24/7;
      
      const dayViews = hourlyData
        .filter((item, idx) => idx >= startHour && idx < endHour)
        .reduce((sum, item) => sum + item.views, 0);
      
      return {
        day,
        閲覧数: dayViews,
        いいね: Math.round(dayViews * 0.3) // デモ用の比率
      };
    });
  };
  
  // モックデータ生成関数（開発用）
  const generateMockWeeklyData = () => {
    const days = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];
    return days.map(day => ({
      day,
      閲覧数: Math.floor(Math.random() * 200) + 100,
      いいね: Math.floor(Math.random() * 150) + 50
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="corporate-dashboard">
      <Typography variant="h4" component="h1" gutterBottom>
        {dashboardData.shop_name || '企業'} ダッシュボード
      </Typography>
      
      <Grid container spacing={2} sx={{ maxWidth: '100%', margin: 0, width: '100%' }}>
        {/* 広告とエンゲージメントのサマリーカード */}
        <Grid item xs={12} md={8}>
          <Paper className="dashboard-paper">
            <Typography variant="h6" className="section-title">
              広告とエンゲージメント
            </Typography>
            <Grid container spacing={2} sx={{ margin: 0, width: '100%' }}>
              <Grid item xs={12} sm={4}>
                <StyledCard className="corp-dashboard-card corp-views-card">
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      総閲覧数
                    </Typography>
                    <StyledTypographyValue variant="h4">
                      {dashboardData.engagement_stats.total_views.toLocaleString()}
                    </StyledTypographyValue>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <StyledCard 
                  className="corp-dashboard-card corp-ads-card"
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                  onClick={() => navigate('/corporate/advertisements')}
                >
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      有効な広告
                    </Typography>
                    <StyledTypographyValue variant="h4">
                      {dashboardData.ads_stats.active_ads.toLocaleString()}
                    </StyledTypographyValue>
                    <Typography variant="caption" color="primary">
                      → 広告管理
                    </Typography>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <StyledCard 
                  className="corp-dashboard-card corp-users-card"
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                  onClick={() => navigate('/corporate/tagged-users')}
                >
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      タグ付きユーザー
                    </Typography>
                    <StyledTypographyValue variant="h4">
                      {dashboardData.linked_users_billing_count || dashboardData.linked_users_count || 0}
                    </StyledTypographyValue>
                    {dashboardData.linked_users_deleted_count > 0 && (
                      <Typography variant="caption" color="textSecondary">
                        （うち削除済 {dashboardData.linked_users_deleted_count}件）
                      </Typography>
                    )}
                    <Typography variant="caption" color="primary">
                      → ユーザー管理
                    </Typography>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12}>
                <StyledCard className="corp-dashboard-card">
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      総いいね数
                    </Typography>
                    <StyledTypographyValue variant="h4">
                      {dashboardData.engagement_stats.total_likes.toLocaleString()}
                    </StyledTypographyValue>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* 広告サマリー */}
        <Grid item xs={12} md={4}>
          <Paper className="dashboard-paper">
            <Typography variant="h6" className="section-title">
              広告サマリー
            </Typography>
            <Grid container spacing={2} sx={{ margin: 0, width: '100%' }}>
              <Grid item xs={12}>
                <StyledCard className="corp-dashboard-card corp-total-ads-card">
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      広告総数
                    </Typography>
                    <StyledTypographyValue variant="h4">
                      {dashboardData.ads_stats.total_ads.toLocaleString()}
                    </StyledTypographyValue>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={6}>
                <StyledCard className="corp-dashboard-card corp-active-ads-card">
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      アクティブ
                    </Typography>
                    <StyledTypographyValue variant="h5">
                      {dashboardData.ads_stats.active_ads.toLocaleString()}
                    </StyledTypographyValue>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={6}>
                <StyledCard className="corp-dashboard-card corp-expired-ads-card">
                  <StyledCardContent>
                    <Typography variant="body2" color="textSecondary">
                      期限切れ
                    </Typography>
                    <StyledTypographyValue variant="h5">
                      {dashboardData.ads_stats.expired_ads.toLocaleString()}
                    </StyledTypographyValue>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* 週間エンゲージメントグラフ */}
        <Grid item xs={12}>
          <Paper className="dashboard-paper">
            <Typography variant="h6" className="section-title">
              週間エンゲージメント
            </Typography>
            <Box sx={{ width: '100%', height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={weeklyData}
                  margin={{
                    top: 20, right: 30, left: 20, bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="閲覧数" fill="#8884d8" />
                  <Bar dataKey="いいね" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CorporateDashboard; 